import Info from '@mdi/svg/svg/information.svg?react';
import classNames from 'classnames';
import capitalize from 'lodash/capitalize';
import React, { memo } from 'react';
import styled from 'styled-components';
import { EMPTY_OBJ } from '../../constants';
import { COLOR, FONT_SIZE, SIZE } from '../../styles';
import { Button } from '../Button';
import { Flex } from '../Flex';
import { MDIIcon } from '../MDIIcon';
import { type AlertCardProps } from './AlertCard.types';

const AlertCard: React.FC<AlertCardProps> = memo(
  ({
    className = '',
    colorMode = 'dark',
    message,
    notificationType = '',
    severity,
    subtitle,
    title,
    onClose,
    templateProps = EMPTY_OBJ,
    time,
  }) => {
    const {
      dismissAction,
      defaultAlertType = 'info',
      dismissText = 'Dismiss',
      viewAction,
      viewText = 'View',
    } = templateProps;

    const handleDismiss = () => {
      dismissAction?.();
      if (onClose) {
        onClose();
      }
    };

    const alertType = notificationType?.length ? capitalize(notificationType) : defaultAlertType;
    const cardWrapperClass = classNames('alert-card', className, colorMode, severity);

    return (
      <AlertCardWrapper className={cardWrapperClass} data-testid="alert-card-wrapper">
        <TitleWrapper className="card-title-wrapper">
          <Flex className="card-title" width="auto">
            <MDIIcon icon={Info} style={{ height: '14px', width: '14px' }} />
            <div data-testid="alert-card-notification-type">{alertType}</div>
          </Flex>
          <div className="card-time" data-testid="alert-card-time">
            {time}
          </div>
        </TitleWrapper>
        <ContentWrapper className="card-content-wrapper">
          <ContentItem data-testid="alert-card-title">{title}</ContentItem>
          <ContentItem data-testid="alert-card-subtitle">{subtitle}</ContentItem>
          <ContentItem data-testid="alert-card-message">{message}</ContentItem>
        </ContentWrapper>
        <ActionsWrapper className="card-actions-wrapper">
          <Button
            className="card-action-button"
            colorMode={colorMode}
            isPrimary={false}
            onClick={handleDismiss}
            text={dismissText}
            type="clear"
          />
          <Button
            className="card-action-button"
            colorMode={colorMode}
            onClick={viewAction}
            text={viewText}
            type="clear"
          />
        </ActionsWrapper>
      </AlertCardWrapper>
    );
  },
);
// Styled Components
const AlertCardWrapper = styled.li`
  background: ${COLOR['dark-gray']};
  border-radius: ${SIZE['4']};
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.5);
  color: ${COLOR['white']};
  list-style: none;
  margin: 0;

  &.error {
    .card-title-wrapper {
      background: ${COLOR['red']};
      color: ${COLOR['white']};

      .icon {
        fill: ${COLOR['white']};
      }
    }
  }

  &.warning {
    .card-title-wrapper {
      background: ${COLOR['yellow']};
      color: ${COLOR['dark-gray-dark']};
      .icon {
        fill: ${COLOR['dark-gray-dark']};
      }
    }
  }

  &.success {
    .card-title-wrapper {
      background: ${COLOR['green']};
    }
  }

  &.info {
    .card-title-wrapper {
      background: ${COLOR['turquoise']};
      color: ${COLOR['dark-gray-dark']};

      .icon {
        fill: ${COLOR['dark-gray-dark']};
      }
    }
  }

  .card-action-button {
    &.styled.clear {
      color: ${COLOR['light-gray-darkest']};
      margin: 0;

      &.mode-dark {
        &:hover,
        &:focus {
          color: ${COLOR['white']};
        }

        &.primary {
          color: ${COLOR['light-gray']};

          &:hover,
          &:focus {
            color: ${COLOR['white']};
          }
        }
      }
    }
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  padding: ${SIZE['10']} ${SIZE['12']};
  align-items: center;
  justify-content: space-between;
  background: ${COLOR['blue-light']};
  border-top-left-radius: ${SIZE['4']};
  border-top-right-radius: ${SIZE['4']};
  color: ${COLOR['white']};
  font-size: ${FONT_SIZE['14']};

  .icon {
    fill: ${COLOR['white']};
    margin-right: 7px;
    margin-top: 1px;
  }

  .card-title {
    text-transform: capitalize;
  }

  .card-time {
    font-size: ${FONT_SIZE['12']};
  }
`;

const ContentWrapper = styled.div`
  padding: ${SIZE['10']} ${SIZE['12']};
  font-size: ${FONT_SIZE['12']};
`;

const ContentItem = styled.div`
  margin-bottom: 1px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${SIZE['10']} ${SIZE['12']};
`;

// Display Name
AlertCard.displayName = 'AlertCard';

export default AlertCard;
